import { compareCompositeAgreement } from "@/metrics/registry"
import type { MetricRecord } from "@/metrics/runtime"

import { annotationHasValue, type Annotation } from "@/types/api/Annotation"
import type { HumanReviewProject } from "@/types/api/HumanReviewProject"
import type { MetricOutputDisplaySource } from "@/types/RunMetricOutput"

import {
  DEFAULT_TOLERANCE_SECONDS,
  getHumanSegments,
  getMetricSegments,
  segmentsAgree,
  segmentsAgreeWithTolerance,
} from "../test-metric-drawer/utils/segmentAgreement"
import type {
  GroupedSimulationData,
  MetricOutputData,
} from "./groupSimulationAnnotations"

export {
  formatDate,
  getHumanReviewValue,
  groupAnnotationsBySimulation,
} from "./groupSimulationAnnotations"
export type {
  GroupedSimulationData,
  HumanReviewData,
  MetricOutputData,
} from "./groupSimulationAnnotations"
export { extractSimulationIds } from "./simulationPresentation"

export const SIMULATION_AGREEMENT_FILTER = {
  ALL: "all",
  AGREE: "agree",
  DISAGREE: "disagree",
} as const

export type AgreementFilter =
  (typeof SIMULATION_AGREEMENT_FILTER)[keyof typeof SIMULATION_AGREEMENT_FILTER]

export const SIMULATION_ORDERING_FILTER = {
  DATE_REVIEWED: "date_reviewed",
  SIM_ID: "sim_id",
} as const

export type OrderingFilter =
  (typeof SIMULATION_ORDERING_FILTER)[keyof typeof SIMULATION_ORDERING_FILTER]

const ANY_REVIEWER_FILTER = "anyone" as const
export type ReviewerFilter = string
export const ALL_PROJECTS_FILTER = "all" as const
export type ProjectFilter = string

export function getAnnotationProjectIds(
  selectedProjectId: string | undefined,
): string[] | undefined {
  return selectedProjectId && selectedProjectId !== ALL_PROJECTS_FILTER
    ? [selectedProjectId]
    : undefined
}

/**
 * Agreement reveals machine-vs-human information. Only a specifically
 * selected non-blind project may show it; mixed/unknown project views use the
 * standalone human result.
 */
export function canRevealHumanVsAiAgreement(
  selectedProjectId: string | undefined,
  projects: HumanReviewProject[] | undefined,
  isProjectScopeFetching = false,
): boolean {
  if (
    isProjectScopeFetching ||
    !selectedProjectId ||
    selectedProjectId === ALL_PROJECTS_FILTER
  ) {
    return false
  }
  const project = projects?.find(
    (candidate) => candidate.ulid === selectedProjectId,
  )
  return !!project && !project.project_rules?.includes("blind_labeling")
}

export function getEffectiveAgreementFilter(
  agreement: AgreementFilter,
  selectedProjectId: string | undefined,
  projects: HumanReviewProject[] | undefined,
  isProjectScopeFetching = false,
): AgreementFilter {
  return canRevealHumanVsAiAgreement(
    selectedProjectId,
    projects,
    isProjectScopeFetching,
  )
    ? agreement
    : SIMULATION_AGREEMENT_FILTER.ALL
}

export interface SimulationFilters {
  agreement: AgreementFilter
  ordering: OrderingFilter
  reviewer: ReviewerFilter
  toleranceSeconds?: number
  project?: string
  includeUnreviewed?: boolean
  includeWithoutMetricOutput?: boolean
  /** Keep only sims with a human review at/after this instant (ISO 8601). */
  labeledAfter?: string
  /** Keep only sims with a human review at/before this instant (ISO 8601). */
  labeledBefore?: string
}

export const DEFAULT_SIMULATION_FILTERS: SimulationFilters = {
  agreement: SIMULATION_AGREEMENT_FILTER.ALL,
  ordering: SIMULATION_ORDERING_FILTER.DATE_REVIEWED,
  reviewer: ANY_REVIEWER_FILTER,
  includeUnreviewed: false,
  includeWithoutMetricOutput: true,
  labeledAfter: undefined,
  labeledBefore: undefined,
}

interface AudioAgreementMetric {
  supportsAudioSegmentReview: MetricRecord["supportsAudioSegmentReview"]
  getAudioRegionReviewConfig: () => { useRegions?: boolean } | null
  getAudioRegions: (
    metricOutput: MetricOutputDisplaySource | undefined,
  ) => ReturnType<MetricRecord["getAudioRegions"]>
  metric_manager?: MetricRecord["metric_manager"]
  metric_metadata?: MetricRecord["metric_metadata"]
  annotationMatchesMetricOutput: (
    annotation: Annotation,
    metricOutput: MetricOutputData,
  ) => boolean
}

/**
 * Filters and sorts grouped simulation data based on the provided filters.
 *
 * For audio metrics that use regions, pass `fullMetricOutputs` (the per-sim
 * metric output map for the current metric) so that agreement is evaluated
 * using segment overlap with `toleranceSeconds`. When omitted, agreement
 * falls back to scalar value comparison.
 */
export const filterAndSortSimulations = (
  groupedData: GroupedSimulationData[],
  filters: SimulationFilters,
  metric: AudioAgreementMetric | null,
  fullMetricOutputs?: Record<
    string,
    MetricOutputDisplaySource | null | undefined
  >,
  humanReviewProjects?: HumanReviewProject[],
  criteriaViewMode: "score" | "criteria" = "criteria",
  annotationsAreProjectScoped = false,
  isProjectScopeFetching = false,
): GroupedSimulationData[] => {
  let data = [...groupedData]
  const effectiveAgreement = getEffectiveAgreementFilter(
    filters.agreement,
    filters.project,
    humanReviewProjects,
    isProjectScopeFetching,
  )

  // Filter by human review project
  if (
    !annotationsAreProjectScoped &&
    filters.project &&
    filters.project !== ALL_PROJECTS_FILTER
  ) {
    const project = humanReviewProjects?.find((p) => p.ulid === filters.project)
    if (!project) {
      return []
    }
    const simIdSet = new Set(project.linked_simulation_ids)
    const assigneeSet = new Set(project.assignees)
    data = data
      .map((item) => {
        if (!simIdSet.has(item.simulationOutputId)) {
          return null
        }
        const projectAnnotations = item.rawAnnotations.filter(
          (a) =>
            simIdSet.has(a.simulation_output_id) && assigneeSet.has(a.assignee),
        )
        if (projectAnnotations.length === 0) {
          return null
        }
        const projectAnnotationIds = new Set(
          projectAnnotations.map((a) => a.id),
        )
        const filteredReviews = item.humanReviews.filter((hr) =>
          projectAnnotationIds.has(hr.id),
        )
        // Recompute primary flag if the global PRIMARY was filtered out
        const hasPrimary = filteredReviews.some((hr) => hr.isCountedAsPrimary)
        const recomputedReviews =
          !hasPrimary && filteredReviews.length > 0
            ? filteredReviews.map((hr, i) => ({
                ...hr,
                isCountedAsPrimary: i === 0,
              }))
            : filteredReviews
        return {
          ...item,
          rawAnnotations: projectAnnotations,
          humanReviews: recomputedReviews,
        }
      })
      .filter((item): item is GroupedSimulationData => item !== null)
  }

  // Filter out items without a meaningful metric output value
  if (filters.includeWithoutMetricOutput === false) {
    data = data.filter((item) => {
      const mo = item.rawMetricOutput
      if (!mo) {
        return false
      }
      return (
        mo.string_value != null ||
        mo.float_value != null ||
        (mo.set_value != null && mo.set_value.length > 0) ||
        (mo.result != null &&
          typeof mo.result === "object" &&
          Object.keys(mo.result).length > 0) ||
        (mo.result_json != null &&
          typeof mo.result_json === "object" &&
          Object.keys(mo.result_json).length > 0)
      )
    })
  }

  // Filter by agreement
  if (effectiveAgreement !== SIMULATION_AGREEMENT_FILTER.ALL && metric) {
    const usesRegions = metric.getAudioRegionReviewConfig()?.useRegions === true
    const isAudioMetric = !!metric.supportsAudioSegmentReview

    data = data.filter((item) => {
      // Audio metrics with full outputs: use segment-based agreement
      if (isAudioMetric && fullMetricOutputs) {
        const fullOutput = fullMetricOutputs[item.simulationOutputId] ?? null
        const primaryAnnotation =
          item.rawAnnotations.find((a) => a.priority === "PRIMARY") ??
          item.rawAnnotations[0] ??
          null
        const metricSegs = getMetricSegments(metric, fullOutput)
        const humanSegs = getHumanSegments(primaryAnnotation)
        const agrees = usesRegions
          ? segmentsAgreeWithTolerance(
              metricSegs,
              humanSegs,
              filters.toleranceSeconds ?? DEFAULT_TOLERANCE_SECONDS,
            )
          : segmentsAgree(metricSegs, humanSegs)
        return effectiveAgreement === SIMULATION_AGREEMENT_FILTER.AGREE
          ? agrees
          : !agrees
      }

      // Scalar agreement using MetricRecord's annotationMatchesMetricOutput
      // Need metric output to determine agreement - filter out if missing
      if (!item.rawMetricOutput) {
        return false
      }

      // Check if any annotation has a value - filter out if none have values
      const annotationsWithValues =
        item.rawAnnotations.filter(annotationHasValue)
      if (annotationsWithValues.length === 0) {
        return false
      }

      // Check if primary annotation agrees with metric output
      const primaryAnnotation =
        annotationsWithValues.find(
          (annotation) => annotation.priority === "PRIMARY",
        ) ?? annotationsWithValues[0]

      // Composite filters use the selected explicit criteria or score mode.
      const isComposite =
        metric.metric_manager === "manager_composite_evaluation"
      if (isComposite) {
        const outcome = compareCompositeAgreement(
          {
            float_value: item.rawMetricOutput.float_value,
            set_value: item.rawMetricOutput.set_value ?? null,
            result: item.rawMetricOutput.result ?? null,
            result_json: item.rawMetricOutput.result_json ?? null,
          },
          {
            float_value: primaryAnnotation.ground_truth_float_value,
            set_value: primaryAnnotation.ground_truth_set_value,
            json_value: primaryAnnotation.ground_truth_json,
          },
          metric.metric_metadata,
          criteriaViewMode,
        )
        if (outcome.kind === "skip") {
          return false
        }
        if (outcome.kind === "per-criterion" && outcome.total === 0) {
          return false
        }
        const agrees =
          outcome.kind === "per-criterion"
            ? outcome.matching === outcome.total
            : outcome.agrees
        return effectiveAgreement === SIMULATION_AGREEMENT_FILTER.AGREE
          ? agrees
          : !agrees
      }

      const agrees = metric.annotationMatchesMetricOutput(
        primaryAnnotation,
        item.rawMetricOutput,
      )

      return effectiveAgreement === SIMULATION_AGREEMENT_FILTER.AGREE
        ? agrees
        : !agrees
    })
  }

  // Filter by reviewer
  if (filters.reviewer !== "anyone") {
    data = data.filter((item) =>
      item.humanReviews.some((review) => review.reviewer === filters.reviewer),
    )
  }

  // Filter by label time (annotation last_updated_at). Sims with no human
  // reviews can't be matched on label time, so they're excluded while active.
  if (filters.labeledAfter || filters.labeledBefore) {
    const parseBound = (iso?: string): number | null => {
      if (!iso) {
        return null
      }
      const ms = new Date(iso).getTime()
      return Number.isNaN(ms) ? null : ms
    }
    const afterMs = parseBound(filters.labeledAfter)
    const beforeMs = parseBound(filters.labeledBefore)
    data = data.filter((item) =>
      item.humanReviews.some((review) => {
        if (!review.dateRaw) {
          return false
        }
        const reviewedMs = new Date(review.dateRaw).getTime()
        if (Number.isNaN(reviewedMs)) {
          return false
        }
        if (afterMs !== null && reviewedMs < afterMs) {
          return false
        }
        if (beforeMs !== null && reviewedMs > beforeMs) {
          return false
        }
        return true
      }),
    )
  }

  // Sort by ordering
  if (filters.ordering === "date_reviewed") {
    data.sort((a, b) => {
      const dateA = String(a.humanReviews[0]?.dateRaw ?? "")
      const dateB = String(b.humanReviews[0]?.dateRaw ?? "")
      return dateB.localeCompare(dateA) // Most recent first
    })
  } else if (filters.ordering === "sim_id") {
    data.sort((a, b) =>
      a.simulationOutputId.localeCompare(b.simulationOutputId),
    )
  }

  return data
}
